import { useCallback, useEffect, useState } from 'react'
import type { PaymentWalletOption, Stripe, StripeElementsUpdateOptions } from '@stripe/stripe-js'
import { PaymentElement, useElements } from '@stripe/react-stripe-js'
import styled from 'styled-components'
import updateLuxPlusPrimaryCard from './updateLuxPlusPrimaryCard'

const StripePaymentElement = styled(PaymentElement)`
  border: none;
`

const stripePaymentElementOptions = {
  layout: 'tabs' as const,
  wallets: {
    applePay: 'never' as PaymentWalletOption,
    googlePay: 'never' as PaymentWalletOption,
    link: 'never' as PaymentWalletOption,
  },
}

interface Props {
  currencyCode: string
  onCompleteChange: (complete: boolean) => void
  onSubmitHandlerChange: (handler?: () => Promise<void>) => void
  purposeId: string
  regionCode: string
  selected: boolean
  stripe: Stripe
}

function LuxPlusNewPrimaryCardElement(props: Props) {
  const {
    currencyCode,
    onCompleteChange,
    onSubmitHandlerChange,
    purposeId,
    regionCode,
    selected,
    stripe,
  } = props
  const elements = useElements()
  const [paymentElementComplete, setPaymentElementComplete] = useState(false)

  const submitNewCard = useCallback(async() => {
    if (!selected || !elements || !stripe) return

    elements.update({ setupFutureUsage: 'off_session' } as StripeElementsUpdateOptions)
    const { error: submitError } = await elements.submit()

    if (submitError) {
      throw new Error(submitError.message)
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
    })

    if (error) {
      throw new Error(error.message)
    }

    await updateLuxPlusPrimaryCard({
      confirmWithSetup: true,
      currencyCode,
      paymentMethodId: paymentMethod.id,
      purposeId,
      regionCode,
      stripe,
    })
  }, [currencyCode, elements, purposeId, regionCode, selected, stripe])

  useEffect(() => {
    onSubmitHandlerChange(selected && paymentElementComplete ? submitNewCard : undefined)

    return () => {
      onSubmitHandlerChange(undefined)
    }
  }, [onSubmitHandlerChange, paymentElementComplete, selected, submitNewCard])

  return <StripePaymentElement
    onChange={event => {
      setPaymentElementComplete(event.complete)
      onCompleteChange(event.complete)
    }}
    options={stripePaymentElementOptions}
  />
}

export default LuxPlusNewPrimaryCardElement
